import { useState, useEffect } from "react";
import axios from "axios";
import { APP_BUILD_VERSION } from "@/buildVersion";

// Shared public data (CMS blocks, business info, services) with a
// sessionStorage cache keyed by build version so repeat page views render
// instantly with the freshest known content — no default-content flash.
const API = `${window.location.origin}/api`;
const VER = `shs_pub_${APP_BUILD_VERSION}`;

const readCache = (key) => {
  try { return JSON.parse(sessionStorage.getItem(`${VER}:${key}`)); } catch { return null; }
};
const writeCache = (key, data) => {
  try { sessionStorage.setItem(`${VER}:${key}`, JSON.stringify(data)); } catch { /* quota */ }
};

// Module-level in-flight dedupe: multiple components mounting together share one request
const inflight = {};
const fetchShared = (key, path) => {
  if (!inflight[key]) {
    inflight[key] = axios.get(`${API}${path}`)
      .then((res) => { writeCache(key, res.data); return res.data; })
      .finally(() => { delete inflight[key]; });
  }
  return inflight[key];
};

export function usePublicData() {
  const [blocks, setBlocks] = useState(() => readCache("content")?.blocks || {});
  const [bizInfo, setBizInfo] = useState(() => readCache("bizinfo") || null);
  const [services, setServices] = useState(() => readCache("services")?.items || null);
  const [ready, setReady] = useState(() => !!readCache("content"));

  useEffect(() => {
    let on = true;
    fetchShared("content", "/site/content")
      .then((d) => { if (on) setBlocks(d?.blocks || {}); })
      .catch(() => {})
      .finally(() => { if (on) setReady(true); });
    fetchShared("bizinfo", "/public/business-info")
      .then((d) => { if (on) setBizInfo(d || null); })
      .catch(() => {});
    fetchShared("services", "/site/services")
      .then((d) => { if (on) setServices(d?.items || []); })
      .catch(() => {});
    return () => { on = false; };
  }, []);

  const cms = (key, fallback) => blocks[key]?.value ?? fallback;
  const cmsHtml = (key, fallback) => ({ __html: blocks[key]?.value ?? fallback });

  return { blocks, bizInfo, services, ready, cms, cmsHtml };
}
